import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  Snackbar,
  IconButton,
  CircularProgress,
  Box,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Grow,
  Slide,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Divider,
  InputAdornment,
  alpha,
  Tooltip,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText as MuiListItemText,
} from "@mui/material";

import {
  Add,
  Edit,
  Delete,
  Inventory2,
  Close,
  CheckCircle,
  Warning,
  Info,
  Error,
  AttachMoney,
  Category,
  Straighten,
  Fastfood,
  Save,
  Cancel,
  Download,
  Layers,
  Kitchen,
  Remove,
  AddCircle,
  Calculate,
  ProductionQuantityLimits,
} from "@mui/icons-material";

import Stock from "../../Services/Stock";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Helper function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
};

// Helper function to parse number with comma or dot
const parseNumber = (value) => {
  if (!value) return 0;
  // Replace comma with dot and parse as float
  const stringValue = value.toString().replace(',', '.');
  return parseFloat(stringValue);
};

// Helper function to format number for display
const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  return value.toString().replace('.', ',');
};

const StyledDialog = ({ children, open, onClose, title, icon: Icon, maxWidth = "sm" }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth
      TransitionComponent={Grow}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
        }
      }}
    >
      <Box sx={{ 
        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
        px: 3,
        py: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5
      }}>
        {Icon && (
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}>
            <Icon />
          </Avatar>
        )}
        <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ 
            ml: "auto",
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </Dialog>
  );
};

const StyledSnackbar = ({ open, onClose, message, severity }) => {
  const icons = {
    success: <CheckCircle sx={{ fontSize: 20 }} />,
    error: <Error sx={{ fontSize: 20 }} />,
    warning: <Warning sx={{ fontSize: 20 }} />,
    info: <Info sx={{ fontSize: 20 }} />
  };

  const colors = {
    success: "#22c55e",
    error: "#ef4444",
    warning: "#f59e0b",
    info: "#3b82f6"
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={4000}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "left" }}
    >
      <Paper
        elevation={6}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          minWidth: 300,
          maxWidth: 400,
          borderRadius: 2,
          borderLeft: `6px solid ${colors[severity]}`,
          background: "white",
          boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          animation: "slideIn 0.3s ease-out",
          "@keyframes slideIn": {
            from: {
              transform: "translateX(100%)",
              opacity: 0
            },
            to: {
              transform: "translateX(0)",
              opacity: 1
            }
          }
        }}
      >
        <Box sx={{ 
          width: 40, 
          height: 40, 
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(colors[severity], 0.1),
          color: colors[severity]
        }}>
          {icons[severity]}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {severity === "success" ? "Succès" : 
             severity === "error" ? "Erreur" : 
             severity === "warning" ? "Attention" : "Information"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

// Component for adding produit intermédiaire (composite) - FIXED VERSION with scrolling
const ProduitIntermediaireDialog = ({ open, onClose, stockList, onSuccess, restaurantId }) => {
  const [step, setStep] = useState(0);
  const [produitName, setProduitName] = useState("");
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(0);

  const [quantiteProduite, setQuantiteProduite] = useState(1);
  const [uniteProduction, setUniteProduction] = useState("kg");
  const [errors, setErrors] = useState({});

  // Calculate total cost of produit intermédiaire
  const calculateTotalCost = () => {
    let total = 0;
    selectedIngredients.forEach(ing => {
      total += (ing.pricePerUnit * ing.quantity);
    });
    setCalculatedPrice(total);
    return total;
  };

  useEffect(() => {
    calculateTotalCost();
  }, [selectedIngredients]);

  // Add ingredient with proper duplicate checking and quantity parsing
  const addIngredient = (stockItem) => {
    const existingIndex = selectedIngredients.findIndex(i => i.id === stockItem.id);
    
    if (existingIndex !== -1) {
      setSelectedIngredients(prev => 
        prev.map((item, index) => 
          index === existingIndex 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setSelectedIngredients(prev => [
        ...prev,
        {
          id: stockItem.id,
          productName: stockItem.productName,
          pricePerUnit: stockItem.pricePerUnit,
          quantity: 1,
          unit: stockItem.unit
        }
      ]);
    }
  };

  const removeIngredient = (ingredientId) => {
    setSelectedIngredients(prev => prev.filter(i => i.id !== ingredientId));
  };

  const updateIngredientQuantity = (ingredientId, newQuantity) => {
    const parsedQuantity = parseNumber(newQuantity);
    if (parsedQuantity <= 0) {
      removeIngredient(ingredientId);
    } else {
      setSelectedIngredients(prev =>
        prev.map(i => i.id === ingredientId
          ? { ...i, quantity: parsedQuantity }
          : i
        )
      );
    }
  };

  const validateProduit = () => {
    const newErrors = {};
    if (!produitName.trim()) newErrors.produitName = "Nom requis";
    if (selectedIngredients.length === 0) newErrors.ingredients = "Au moins un ingrédient requis";
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateProduit()) return;

    setLoading(true);
    try {
      const produitIntermediaire = {
        productName: produitName,
        isComposite: true,
        isProduitIntermediaire: true,
        components: selectedIngredients.map(ing => ({
          id: ing.id,
          productName: ing.productName,
          quantity: ing.quantity,
          unit: ing.unit,
          pricePerUnit: ing.pricePerUnit
        })),
        pricePerUnit: calculatedPrice / quantiteProduite,
        totalCost: calculatedPrice,
        quantiteProduite: quantiteProduite,
        uniteProduction: uniteProduction,
        unit: uniteProduction,
        restaurantId: restaurantId,
        date: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        quantity: quantiteProduite
      };

      await Stock.addProduitIntermediaire(produitIntermediaire);
      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error("Error adding produit intermédiaire:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setProduitName("");
    setSelectedIngredients([]);
    setStep(0);
    setCalculatedPrice(0);
    setQuantiteProduite(1);
    setUniteProduction("kg");
    setErrors({});
  };

  const getAvailableIngredients = () => {
    return stockList.filter(s => 
      !s.isComposite && 
      !selectedIngredients.some(selected => selected.id === s.id)
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetForm();
        onClose();
      }}
      maxWidth="md"
      fullWidth
      TransitionComponent={Grow}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      {/* Header - Fixed */}
      <Box sx={{ 
        background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        px: 3,
        py: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexShrink: 0
      }}>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}>
          <ProductionQuantityLimits />
        </Avatar>
        <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
          Créer un Ingrédient intermédiaire
        </Typography>
        <IconButton onClick={() => {
          resetForm();
          onClose();
        }} sx={{ ml: "auto", color: "white" }}>
          <Close />
        </IconButton>
      </Box>

      {/* Content - Scrollable */}
      <Box sx={{ 
        p: 3, 
        flex: 1, 
        overflow: "auto",
        display: "flex",
        flexDirection: "column"
      }}>
        <Stepper activeStep={step} sx={{ mb: 4, flexShrink: 0 }}>
          <Step>
            <StepLabel>Informations générales</StepLabel>
          </Step>
          <Step>
            <StepLabel>Sélection ingrédients</StepLabel>
          </Step>
          <Step>
            <StepLabel>Récapitulatif</StepLabel>
          </Step>
        </Stepper>

        {step === 0 && (
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Nom de l'Ingrédient intermédiaire"
              value={produitName}
              onChange={(e) => setProduitName(e.target.value)}
              error={!!errors.produitName}
              helperText={errors.produitName}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Fastfood sx={{ color: "#f59e0b" }} />
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              fullWidth
              label="Quantité produite (poids/volume)"
              type="text"
              value={formatNumber(quantiteProduite)}
              onChange={(e) => setQuantiteProduite(parseNumber(e.target.value) || 0)}
              error={!!errors.quantiteProduite}
              helperText={errors.quantiteProduite || "Exemple: 2,5 ou 2.5"}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Category sx={{ color: "#f59e0b" }} />
                  </InputAdornment>
                )
              }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Unité de production</InputLabel>
              <Select
                value={uniteProduction}
                onChange={(e) => setUniteProduction(e.target.value)}
                label="Unité de production"
                startAdornment={
                  <InputAdornment position="start">
                    <Straighten sx={{ color: "#f59e0b" }} />
                  </InputAdornment>
                }
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="kg">Kilogramme (kg)</MenuItem>
                <MenuItem value="g">Gramme (g)</MenuItem>
                <MenuItem value="l">Litre (L)</MenuItem>
                <MenuItem value="ml">Millilitre (ml)</MenuItem>
                <MenuItem value="piece">Pièce</MenuItem>
                <MenuItem value="portion">Portion</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => setStep(1)}
                disabled={!produitName.trim()}
                sx={{ 
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)"
                  }
                }}
              >
                Suivant
              </Button>
            </Box>
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, flexShrink: 0 }}>
              Ingrédients sélectionnés ({selectedIngredients.length})
            </Typography>
            
            {selectedIngredients.length > 0 && (
              <List sx={{ 
                mb: 2, 
                bgcolor: "#f8fafc", 
                borderRadius: 2, 
                maxHeight: 200, 
                overflow: "auto",
                flexShrink: 0
              }}>
                {selectedIngredients.map((ing) => (
                  <ListItem key={ing.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: alpha("#f59e0b", 0.1), color: "#f59e0b" }}>
                        <Kitchen />
                      </Avatar>
                    </ListItemAvatar>
                    <MuiListItemText
                      primary={ing.productName}
                      secondary={`${ing.pricePerUnit.toFixed(3)} TND / ${ing.unit}`}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => updateIngredientQuantity(ing.id, ing.quantity - 0.1)}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <TextField
                        size="small"
                        value={formatNumber(ing.quantity)}
                        onChange={(e) => updateIngredientQuantity(ing.id, e.target.value)}
                        sx={{ width: 100 }}
                        type="text"
                        InputProps={{ 
                          inputProps: { step: 0.1 },
                          sx: { textAlign: "center" }
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => updateIngredientQuantity(ing.id, ing.quantity + 0.1)}
                      >
                        <AddCircle fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeIngredient(ing.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mt: 2, flexShrink: 0 }}>
              Ajouter des ingrédients
            </Typography>
            
            <Box sx={{ 
              flex: 1, 
              minHeight: 0,
              overflow: "auto",
              mb: 2
            }}>
              {getAvailableIngredients().length === 0 ? (
                <Paper sx={{ p: 3, textAlign: "center", bgcolor: "#f8fafc" }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedIngredients.length === stockList.filter(s => !s.isComposite).length
                      ? "Tous les ingrédients ont été sélectionnés"
                      : "Aucun ingrédient disponible"}
                  </Typography>
                </Paper>
              ) : (
                getAvailableIngredients().map((stock) => (
                  <Card 
                    key={stock.id} 
                    sx={{ 
                      mb: 1, 
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateX(4px)",
                        borderColor: "#f59e0b",
                        boxShadow: "0 2px 8px rgba(245,158,11,0.2)"
                      }
                    }} 
                  >
                    <CardContent 
                      sx={{ 
                        py: 1, 
                        "&:last-child": { pb: 1 },
                        cursor: "pointer"
                      }}
                      onClick={() => addIngredient(stock)}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {stock.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stock.pricePerUnit.toFixed(3)} TND / {stock.unit}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={(e) => {
                            e.stopPropagation();
                            addIngredient(stock);
                          }}
                          sx={{ 
                            borderRadius: 2,
                            borderColor: "#f59e0b",
                            color: "#f59e0b",
                            "&:hover": {
                              borderColor: "#d97706",
                              backgroundColor: alpha("#f59e0b", 0.04)
                            }
                          }}
                        >
                          Ajouter
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>

            {errors.ingredients && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: "block", flexShrink: 0 }}>
                {errors.ingredients}
              </Typography>
            )}

            <Box sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "flex-end", flexShrink: 0 }}>
              <Button onClick={() => setStep(0)}>Retour</Button>
              <Button
                variant="contained"
                onClick={() => setStep(2)}
                disabled={selectedIngredients.length === 0}
                sx={{
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)"
                  }
                }}
              >
                Suivant
              </Button>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              {produitName}
            </Typography>
            
            <Paper sx={{ p: 2, mb: 2, bgcolor: "#f8fafc" }}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Informations de production:
              </Typography>
           
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="body2">Quantité produite:</Typography>
                <Typography variant="body2" fontWeight={500}>{quantiteProduite} {uniteProduction}</Typography>
              </Box>
              
              <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ mt: 2 }}>
                Ingrédients nécessaires:
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                {selectedIngredients.map((ing) => (
                  <Box key={ing.id} sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">
                      {ing.productName}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatNumber(ing.quantity)} {ing.unit} × {ing.pricePerUnit.toFixed(3)} TND = {(ing.quantity * ing.pricePerUnit).toFixed(3)} TND
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Typography fontWeight={600}>Coût total de production:</Typography>
                <Typography fontWeight={600} color="#f59e0b">
                  {calculatedPrice.toFixed(3)} TND
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Typography fontWeight={600}>Coût par {uniteProduction}:</Typography>
                <Typography fontWeight={600} color="#22c55e">
                  {(calculatedPrice / quantiteProduite).toFixed(3)} TND / {uniteProduction}
                </Typography>
              </Box>
            </Paper>

            <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={() => setStep(1)}>Retour</Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                sx={{
                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)"
                  }
                }}
              >
                Créer le produit intermédiaire
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

const StockScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [stockList, setStockList] = useState([]);
  const [stockData, setStockData] = useState({
    productName: "",
    pricePerUnit: "",
    quantity: "",
    unit: "piece",
  });

  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openProduitDialog, setOpenProduitDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const [editStockId, setEditStockId] = useState(null);
  const [deleteStockId, setDeleteStockId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const restaurantId = localStorage.getItem("restaurantId");

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const stocks = await Stock.getAllStocks(restaurantId);
      setStockList(stocks);
    } catch (error) {
      console.error("Error fetching stocks:", error);
      showSnackbar("Erreur lors du chargement des stocks", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStockData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setStockData({
      productName: "",
      pricePerUnit: "",
      quantity: "",
      unit: "piece",
    });
    setEditStockId(null);
    setOpenDialog(false);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    const { productName, pricePerUnit, quantity } = stockData;

    if (!productName?.trim()) errors.productName = "Le nom du produit est requis";
    
    const priceValue = parseNumber(pricePerUnit);
    if (!pricePerUnit || priceValue <= 0) errors.pricePerUnit = "Le prix doit être supérieur à 0";
    
    const quantityValue = parseNumber(quantity);
    if (!quantity || quantityValue <= 0) errors.quantity = "La quantité doit être supérieure à 0";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar("Veuillez corriger les erreurs du formulaire", "error");
      return;
    }

    try {
      const { productName, pricePerUnit, quantity, unit } = stockData;
      const numericPrice = parseNumber(pricePerUnit);
      const numericQuantity = parseNumber(quantity);

      if (editStockId) {
        const updatedData = {
          productName: productName,
          pricePerUnit: numericPrice.toFixed(3),
          quantity: numericQuantity,
          unit: Stock.normalizeUnit(unit),
          date: new Date().toISOString()
        };

        await Stock.updateStock(editStockId, updatedData);
        showSnackbar("Stock mis à jour avec succès", "success");
      } else {
        const stock = new Stock(
          productName,
          numericPrice.toFixed(3),
          new Date().toISOString(),
          numericQuantity,
          Stock.normalizeUnit(unit),
          restaurantId
        );

        await stock.addStock();
        showSnackbar("Produit ajouté avec succès", "success");
      }

      resetForm();
      fetchStocks();
    } catch (error) {
      console.error("Error submitting form:", error);
      showSnackbar("Erreur lors de l'opération", "error");
    }
  };

  // FIXED: Corrected openEditDialog function
  const openEditDialog = (stock) => {
    setStockData({
      productName: stock.productName,
      pricePerUnit: formatNumber(stock.pricePerUnit),
      quantity: formatNumber(stock.quantity),
      unit: stock.unit,
    });
    setEditStockId(stock.id);
    setOpenDialog(true);
  };

  const openDeleteDialog = (stockId) => {
    setDeleteStockId(stockId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteStockId(null);
  };

  const handleDelete = async () => {
    if (!deleteStockId) return;
    
    setDeleteLoading(true);
    
    try {
      await Stock.deleteStock(deleteStockId);
      showSnackbar("Produit supprimé avec succès", "success");
      await fetchStocks();
      closeDeleteDialog();
    } catch (error) {
      console.error("Error deleting stock:", error);
      showSnackbar("Erreur lors de la suppression", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const prepareExportData = () => {
    return stockList.map(stock => {
      const totalValue = parseNumber(stock.pricePerUnit) * parseNumber(stock.quantity);
      
      const exportObj = {
        'Produit': stock.productName,
        'Type': stock.isProduitIntermediaire ? 'Ingrédient Intermédiaire' : (stock.isComposite ? 'Composite' : 'Simple'),
        'Unité': stock.unit?.toUpperCase() || '',
        'Prix unitaire (TND)': parseNumber(stock.pricePerUnit).toFixed(3),
        'Quantité': parseNumber(stock.quantity),
        'Valeur totale (TND)': totalValue.toFixed(3),
        'Date mise à jour': formatDate(stock.updatedAt || stock.createdAt),
      };
      
      if (stock.isProduitIntermediaire) {
        exportObj['Nombre de portions'] = stock.nombrePortions || '';
        exportObj['Quantité produite'] = stock.quantiteProduite || '';
        exportObj['Unité production'] = stock.uniteProduction || '';
        exportObj['Ingrédients'] = stock.components?.map(c => `${c.productName} (${c.quantity} ${c.unit})`).join(', ') || '';
      }
      
      return exportObj;
    });
  };

  const exportToExcel = () => {
    try {
      setExportLoading(true);
      
      const dataToExport = prepareExportData();
      const fileName = `stock_${new Date().toISOString().split('T')[0]}.xlsx`;
      const sheetName = 'Stock';

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const maxWidth = [];
      dataToExport.forEach(row => {
        Object.keys(row).forEach((key, i) => {
          const cellValue = row[key] ? row[key].toString() : '';
          maxWidth[i] = Math.max(maxWidth[i] || 0, cellValue.length);
        });
      });
      ws['!cols'] = maxWidth.map(w => ({ wch: Math.min(w + 2, 50) }));

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      saveAs(data, fileName);
      showSnackbar('Export réussi', 'success');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showSnackbar('Erreur lors de l\'export Excel', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchStocks();
    }
  }, [restaurantId]);

  const filteredStocks = tabValue === 0 
    ? stockList 
    : tabValue === 1 
      ? stockList.filter(s => !s.isComposite && !s.isProduitIntermediaire)
      : stockList.filter(s => s.isProduitIntermediaire || s.isComposite);

  const MobileStockCard = ({ stock }) => {
    const total = parseNumber(stock.pricePerUnit) * parseNumber(stock.quantity);
    const isProduitIntermediaire = stock.isProduitIntermediaire || stock.isComposite;
    
    return (
      <Card sx={{ mb: 2, borderRadius: 3, overflow: "hidden" }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: isProduitIntermediaire ? "#f59e0b" : "#2563eb" }}>
              {isProduitIntermediaire ? <ProductionQuantityLimits /> : <Fastfood />}
            </Avatar>
          }
          title={
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {stock.productName}
              {isProduitIntermediaire && (
                <Chip
                  label="Ingrédient Intermédiaire"
                  size="small"
                  sx={{ ml: 1, bgcolor: "#f59e0b", color: "white", height: 20, fontSize: "0.7rem" }}
                />
              )}
            </Typography>
          }
          subheader={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
              <Chip
                label={stock.unit?.toUpperCase() || ''}
                size="small"
                sx={{
                  background: "#3b82f6",
                  color: "white",
                  fontWeight: 600,
                  height: 20,
                  fontSize: "0.7rem"
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {formatDate(stock.updatedAt || stock.createdAt)}
              </Typography>
            </Box>
          }
        />
        <CardContent>
          {isProduitIntermediaire && stock.components && (
            <Box sx={{ mb: 2, p: 1, bgcolor: "#fef3c7", borderRadius: 2 }}>
              <Typography variant="caption" fontWeight={600}>
                Ingrédients:
              </Typography>
              {stock.components.map((comp, idx) => (
                <Typography key={idx} variant="caption" display="block" color="text.secondary">
                  • {comp.productName}: {formatNumber(comp.quantity)} {comp.unit}
                </Typography>
              ))}
              {stock.nombrePortions && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  📊 {stock.nombrePortions} portions | {stock.quantiteProduite} {stock.uniteProduction}
                </Typography>
              )}
            </Box>
          )}
          
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quantité
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatNumber(stock.quantity)} {stock.unit}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Prix unitaire
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#2563eb" }}>
                {parseNumber(stock.pricePerUnit).toFixed(3)} TND
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Valeur totale
            </Typography>
            <Chip
              label={`${total.toFixed(3)} TND`}
              sx={{
                background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                color: "white",
                fontWeight: 700,
              }}
            />
          </Box>
          
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={() => openEditDialog(stock)}
              sx={{ borderRadius: 2 }}
            >
              Modifier
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<Delete />}
              onClick={() => openDeleteDialog(stock.id)}
              sx={{ borderRadius: 2 }}
            >
              Supprimer
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 }, 
      maxWidth: 1400, 
      margin: "auto",
      minHeight: "100vh",
      background: "#f8fafc"
    }}>
      {/* HEADER */}
      <Fade in timeout={1000}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 4,
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            border: "1px solid rgba(37, 99, 235, 0.1)"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "#2563eb", width: 50, height: 50 }}>
              <Inventory2 />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  background: "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}
              >
                Gestion des Stocks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Suivi en temps réel des approvisionnements et ingrédients intermédiaires
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<ProductionQuantityLimits />}
              onClick={() => setOpenProduitDialog(true)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                borderColor: "#f59e0b",
                color: "#f59e0b",
                "&:hover": {
                  borderColor: "#d97706",
                  backgroundColor: alpha("#f59e0b", 0.04)
                }
              }}
            >
              Ingrédient Intermédiaire
            </Button>

            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={exportToExcel}
              disabled={stockList.length === 0 || exportLoading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                borderColor: "#2563eb",
                color: "#2563eb",
                "&:hover": {
                  borderColor: "#1d4ed8",
                  backgroundColor: alpha("#2563eb", 0.04)
                }
              }}
            >
              {exportLoading ? <CircularProgress size={24} /> : "Exporter"}
            </Button>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: 1.5,
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                boxShadow: "0 4px 15px rgba(37, 99, 235, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(37, 99, 235, 0.4)"
                },
                transition: "all 0.3s ease"
              }}
            >
              Nouvel Ingrédient
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* TABS */}
      <Tabs 
        value={tabValue} 
        onChange={(e, v) => setTabValue(v)} 
        sx={{ 
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9rem'
          }
        }}
      >
        <Tab label="Tous les Ingrédients" />
        <Tab label="Matières premières" />
        <Tab label="Ingrédients intermédiaires" />
      </Tabs>

      {/* TABLE / CARDS */}
      {loading ? (
        <Box sx={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: 400,
          flexDirection: "column",
          gap: 2
        }}>
          <CircularProgress size={60} thickness={4} sx={{ color: "#2563eb" }} />
          <Typography variant="body1" color="text.secondary">
            Chargement des stocks...
          </Typography>
        </Box>
      ) : (
        <Fade in timeout={800}>
          <Box>
            {isMobile ? (
              <Box>
                {filteredStocks.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
                    <Inventory2 sx={{ fontSize: 60, color: "#94a3b8", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Aucun produit trouvé
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setOpenDialog(true)}
                      sx={{ mt: 2, borderRadius: 2 }}
                    >
                      Ajouter votre premier produit
                    </Button>
                  </Paper>
                ) : (
                  filteredStocks.map(stock => (
                    <MobileStockCard key={stock.id} stock={stock} />
                  ))
                )}
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(37, 99, 235, 0.1)"
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                        "& th": {
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          py: 2
                        }
                      }}
                    >
                      <TableCell>Ingrédient</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Unité</TableCell>
                      <TableCell align="right">Prix unitaire</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Quantité</TableCell>
                      <TableCell align="right">Valeur Totale</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStocks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                          <Inventory2 sx={{ fontSize: 60, color: "#94a3b8", mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            Aucun produit trouvé
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStocks.map((stock) => {
                        const total = parseNumber(stock.pricePerUnit) * parseNumber(stock.quantity);
                        const isProduitIntermediaire = stock.isProduitIntermediaire || stock.isComposite;
                        
                        return (
                          <TableRow 
                            key={stock.id} 
                            hover
                            sx={{
                              "&:hover": {
                                bgcolor: alpha("#2563eb", 0.04)
                              }
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Tooltip title={isProduitIntermediaire ? "Ingrédient intermédiaire" : "Matière première"}>
                                  <Avatar sx={{ 
                                    width: 32, 
                                    height: 32,
                                    bgcolor: isProduitIntermediaire ? alpha("#f59e0b", 0.1) : alpha("#2563eb", 0.1)
                                  }}>
                                    {isProduitIntermediaire ? <ProductionQuantityLimits sx={{ fontSize: 18, color: "#f59e0b" }} /> : <Fastfood sx={{ fontSize: 18, color: "#2563eb" }} />}
                                  </Avatar>
                                </Tooltip>
                                <Typography sx={{ fontWeight: 500 }}>
                                  {stock.productName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={isProduitIntermediaire ? "Ingrédient Intermédiaire" : "Matière première"}
                                size="small"
                                sx={{
                                  background: isProduitIntermediaire 
                                    ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                                    : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  height: 24
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={stock.unit?.toUpperCase() || ''}
                                size="small"
                                sx={{
                                  background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                  height: 24
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#2563eb" }}>
                                  TND
                                </Typography>
                                <Typography sx={{ fontWeight: 500 }}>
                                  {parseNumber(stock.pricePerUnit).toFixed(3)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(stock.updatedAt || stock.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={formatNumber(stock.quantity)}
                                sx={{
                                  bgcolor: alpha("#2563eb", 0.1),
                                  color: "#2563eb",
                                  fontWeight: 700
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${total.toFixed(3)} TND`}
                                sx={{
                                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                                  color: "white",
                                  fontWeight: 700,
                                  px: 1
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                <Tooltip title="Modifier">
                                  <IconButton
                                    onClick={() => openEditDialog(stock)}
                                    sx={{
                                      bgcolor: alpha("#2563eb", 0.1),
                                      color: "#2563eb",
                                      "&:hover": {
                                        bgcolor: "#2563eb",
                                        color: "white",
                                        transform: "scale(1.1)"
                                      },
                                      transition: "all 0.2s ease"
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <IconButton
                                    onClick={() => openDeleteDialog(stock.id)}
                                    sx={{
                                      bgcolor: alpha("#ef4444", 0.1),
                                      color: "#ef4444",
                                      "&:hover": {
                                        bgcolor: "#ef4444",
                                        color: "white",
                                        transform: "scale(1.1)"
                                      },
                                      transition: "all 0.2s ease"
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Fade>
      )}

      {/* CREATE/EDIT DIALOG */}
      <StyledDialog
        open={openDialog}
        onClose={resetForm}
        title={editStockId ? "Modifier Ingrédient" : "Ajouter un nouvel Ingrédient"}
        icon={editStockId ? Edit : Add}
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nom de l'Ingrédient"
            name="productName"
            value={stockData.productName}
            onChange={handleInputChange}
            error={!!formErrors.productName}
            helperText={formErrors.productName}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Fastfood sx={{ color: "#2563eb" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2563eb"
                  }
                }
              }
            }}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Unité de mesure</InputLabel>
            <Select
              name="unit"
              value={stockData.unit}
              onChange={handleInputChange}
              label="Unité de mesure"
              startAdornment={
                <InputAdornment position="start">
                  <Straighten sx={{ color: "#2563eb" }} />
                </InputAdornment>
              }
              sx={{ borderRadius: 2 }}
            >
              {Stock.getAvailableUnits().map(unit => (
                <MenuItem key={unit.value} value={unit.value}>
                  {unit.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Prix unitaire (TND)"
            name="pricePerUnit"
            type="text"
            value={stockData.pricePerUnit}
            onChange={handleInputChange}
            error={!!formErrors.pricePerUnit}
            helperText={formErrors.pricePerUnit || "Utilisez . ou , pour les décimales"}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoney sx={{ color: "#2563eb" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />

          <TextField
            fullWidth
            label="Quantité"
            name="quantity"
            type="text"
            value={stockData.quantity}
            onChange={handleInputChange}
            error={!!formErrors.quantity}
            helperText={formErrors.quantity || "Utilisez . ou , pour les décimales"}
            margin="normal"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Category sx={{ color: "#2563eb" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />

          <Box sx={{ 
            display: "flex", 
            gap: 2, 
            justifyContent: "flex-end", 
            mt: 3 
          }}>
            <Button
              onClick={resetForm}
              variant="outlined"
              startIcon={<Cancel />}
              sx={{ 
                borderRadius: 2,
                textTransform: "none",
                px: 3,
                py: 1
              }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              sx={{ 
                borderRadius: 2,
                textTransform: "none",
                px: 3,
                py: 1,
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)"
                }
              }}
            >
              {editStockId ? "Mettre à jour" : "Enregistrer"}
            </Button>
          </Box>
        </form>
      </StyledDialog>

      {/* PRODUIT INTERMEDIAIRE DIALOG */}
      <ProduitIntermediaireDialog
        open={openProduitDialog}
        onClose={() => setOpenProduitDialog(false)}
        stockList={stockList}
        onSuccess={() => {
          fetchStocks();
          showSnackbar("Ingrédient intermédiaire créé avec succès", "success");
        }}
        restaurantId={restaurantId}
      />

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        TransitionComponent={Grow}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            maxWidth: 400,
            width: "100%"
          }
        }}
      >
        <Box sx={{ 
          background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5
        }}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}>
            <Warning />
          </Avatar>
          <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
            Confirmer la suppression
          </Typography>
          <IconButton
            onClick={closeDeleteDialog}
            sx={{ 
              ml: "auto",
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
            }}
          >
            <Close />
          </IconButton>
        </Box>
        
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Avatar 
            sx={{ 
              width: 70, 
              height: 70, 
              bgcolor: alpha("#ef4444", 0.1),
              color: "#ef4444",
              margin: "0 auto 16px"
            }}
          >
            <Warning sx={{ fontSize: 40 }} />
          </Avatar>
          
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Êtes-vous sûr ?
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Cette action est irréversible. L'ingrédient sera définitivement supprimé de votre inventaire.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              onClick={closeDeleteDialog}
              variant="outlined"
              disabled={deleteLoading}
              sx={{ 
                borderRadius: 2, 
                px: 3,
                py: 1,
                flex: 1
              }}
            >
              Annuler
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={deleteLoading}
              sx={{ 
                borderRadius: 2, 
                px: 3,
                py: 1,
                flex: 1,
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)"
                }
              }}
            >
              {deleteLoading ? <CircularProgress size={24} color="inherit" /> : "Supprimer"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* SNACKBAR */}
      <StyledSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default StockScreen;